import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { Plus } from 'lucide-react'
import { FeedbackContext } from '../../../../../contexts/FeedbackContext'
import { ApprovalApi } from '../../../ProductComposition/ApprovationModal/api'
import {
  ProductApprovalListItem,
  ProductApprovalStatus,
  ProductApprovalType,
} from '../../../ProductComposition/ApprovationModal/types'
import {
  EmptyState,
  Input,
  InlineFormRow,
  Label,
  NewApprovalForm,
  PrimaryButton,
  StatusBadge,
  Table,
  TableWrapper,
  Td,
  Th,
  ToolbarRow,
  Tr,
} from '../../../ProductComposition/ApprovationModal/styles'
import ApprovationModal from '../../../ProductComposition/ApprovationModal'

interface Props {
  productId: number
}

const statusLabel: Record<ProductApprovalStatus, string> = {
  IN_PROGRESS: 'Em andamento',
  APPROVED: 'Aprovado',
  REJECTED: 'Reprovado',
}

const formatDate = (iso: string | null) => {
  if (!iso) return '—'
  return new Date(iso).toLocaleDateString('pt-BR')
}

const isPastDeadline = (iso: string | null) => {
  if (!iso) return false
  return new Date(iso).getTime() < Date.now()
}

const suggestNextName = (
  rows: ProductApprovalListItem[],
  type: ProductApprovalType,
) => {
  if (type === 'SHOWROOM') return 'Mostruário'
  const pilots = rows.filter((r) => r.type === 'PILOT')
  return `Piloto ${pilots.length + 1}`
}

const ApprovationsTable: React.FC<Props> = ({ productId }) => {
  const { setFeedback } = useContext(FeedbackContext)
  const [rows, setRows] = useState<ProductApprovalListItem[]>([])
  const [loading, setLoading] = useState(false)
  const [openId, setOpenId] = useState<number | null>(null)
  const [creating, setCreating] = useState(false)
  const [newName, setNewName] = useState('')
  const [newType, setNewType] = useState<ProductApprovalType>('PILOT')
  const [creatingBusy, setCreatingBusy] = useState(false)

  const reload = useCallback(async () => {
    if (!productId) return
    setLoading(true)
    try {
      const data = await ApprovalApi.list(productId)
      setRows(data)
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao carregar aprovações.',
        statusOk: false,
      })
    } finally {
      setLoading(false)
    }
  }, [productId, setFeedback])

  useEffect(() => {
    reload()
  }, [reload])

  const startCreate = () => {
    setNewType('PILOT')
    setNewName(suggestNextName(rows, 'PILOT'))
    setCreating(true)
  }

  useEffect(() => {
    if (creating) {
      setNewName(suggestNextName(rows, newType))
    }
  }, [newType, creating, rows])

  const handleCreate = async () => {
    if (!newName.trim()) return
    setCreatingBusy(true)
    try {
      const created = await ApprovalApi.create({
        productId,
        name: newName.trim(),
        type: newType,
      })
      await reload()
      setCreating(false)
      setOpenId(created.id)
    } catch (err: any) {
      setFeedback({
        isOpen: true,
        message: err?.response?.data?.message ?? 'Erro ao criar aprovação.',
        statusOk: false,
      })
    } finally {
      setCreatingBusy(false)
    }
  }

  const sortedRows = useMemo(
    () =>
      [...rows].sort((a, b) => {
        if (a.type !== b.type) return a.type === 'PILOT' ? -1 : 1
        return a.id - b.id
      }),
    [rows],
  )

  return (
    <div>
      <ToolbarRow>
        {!creating && (
          <PrimaryButton type='button' onClick={startCreate}>
            <Plus size={14} /> Nova aprovação
          </PrimaryButton>
        )}
      </ToolbarRow>

      {creating && (
        <NewApprovalForm>
          <InlineFormRow>
            <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 200 }}>
              <Label>Nome</Label>
              <Input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                placeholder='Ex.: Piloto 1'
                disabled={creatingBusy}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', minWidth: 160 }}>
              <Label>Tipo</Label>
              <select
                value={newType}
                onChange={(e) => setNewType(e.target.value as ProductApprovalType)}
                disabled={creatingBusy}
                style={{
                  border: '1px solid #d1d5db',
                  borderRadius: 6,
                  padding: '8px 10px',
                  fontSize: 13,
                  background: '#fff',
                }}
              >
                <option value='PILOT'>Piloto</option>
                <option value='SHOWROOM'>Mostruário</option>
              </select>
            </div>
            <PrimaryButton type='button' onClick={handleCreate} disabled={creatingBusy || !newName.trim()}>
              Criar e abrir
            </PrimaryButton>
            <button
              type='button'
              onClick={() => setCreating(false)}
              disabled={creatingBusy}
              style={{
                background: 'none',
                border: 'none',
                color: '#6b7280',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
                padding: '8px 10px',
              }}
            >
              Cancelar
            </button>
          </InlineFormRow>
        </NewApprovalForm>
      )}

      <TableWrapper>
        <Table>
          <thead>
            <tr>
              <Th>Nome</Th>
              <Th>Status</Th>
              <Th>Fornecedor</Th>
              <Th>Criado por</Th>
              <Th>Aprovador</Th>
              <Th>Data início</Th>
              <Th>Entrega Prevista</Th>
              <Th>Valid./Tam.</Th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr>
                <Td colSpan={8}>
                  <EmptyState>Carregando aprovações...</EmptyState>
                </Td>
              </tr>
            )}
            {!loading && sortedRows.length === 0 && (
              <tr>
                <Td colSpan={8}>
                  <EmptyState>
                    Nenhuma aprovação cadastrada ainda. Clique em &quot;Nova aprovação&quot; para começar.
                  </EmptyState>
                </Td>
              </tr>
            )}
            {!loading &&
              sortedRows.map((row) => (
                <Tr key={row.id} $clickable onClick={() => setOpenId(row.id)}>
                  <Td style={{ fontWeight: 700 }}>{row.name}</Td>
                  <Td>
                    <StatusBadge $status={row.status}>{statusLabel[row.status]}</StatusBadge>
                  </Td>
                  <Td>{row.SupplierCompany?.companyName ?? '—'}</Td>
                  <Td>{row.CreatedBy?.name ?? '—'}</Td>
                  <Td>{row.Approver?.name ?? '—'}</Td>
                  <Td>{formatDate(row.startDate)}</Td>
                  <Td
                    style={{
                      color:
                        row.status !== 'APPROVED' && isPastDeadline(row.expectedDeliveryDate)
                          ? '#dc2626'
                          : undefined,
                      fontWeight:
                        row.status !== 'APPROVED' && isPastDeadline(row.expectedDeliveryDate)
                          ? 600
                          : undefined,
                    }}
                  >
                    {formatDate(row.expectedDeliveryDate)}
                  </Td>
                  <Td>{(row.sizes ?? []).join(', ') || '—'}</Td>
                </Tr>
              ))}
          </tbody>
        </Table>
      </TableWrapper>

      {openId && (
        <ApprovationModal
          approvalId={openId}
          onClose={() => {
            setOpenId(null)
            reload()
          }}
          onChanged={reload}
        />
      )}
    </div>
  )
}

export default ApprovationsTable
